import React, { useState, forwardRef } from 'react';
import Layout from '../../../components/layout/AppLayout';
import Header from '../../../../shared/components/layout/Header';
import ConsumptionList from './components/ConsumptionList';
import ConsumptionInsight from './components/ConsumptionInsight';
import { inventoryReportData } from './dummyData';
import { useSidebar } from '../../../../shared/contexts/SidebarContext';
import { ChevronDown, Calendar, Check } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <button
        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors text-gray-700 min-w-[200px] justify-between"
        onClick={onClick}
        ref={ref}
    >
        <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-sm">{value}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500 ml-2" />
    </button>
));

CustomDateInput.displayName = 'CustomDateInput';

const InventoryReport = () => {
    const { collapsed } = useSidebar();
    const [selectedProject, setSelectedProject] = useState('Vedic Lifestyle');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedStage, setSelectedStage] = useState('All Stages');
    const [selectedBlock, setSelectedBlock] = useState('All Blocks');

    const [isProjectOpen, setIsProjectOpen] = useState(false);
    const [isStageOpen, setIsStageOpen] = useState(false);
    const [isBlockOpen, setIsBlockOpen] = useState(false);

    const projects = ["Vedic Lifestyle"];
    const stages = ["All Stages", "Structural", "Finishing", "Plumbing", "Electrical", "Brickwork"];
    const blocks = ["All Blocks", "Block A", "Block B", "Block C", "Common Area"];

    return (
        <Layout>
            <div className="bg-[#F8FAFC] min-h-screen pb-10">
                {/* Header Section */}
                <div className="flex flex-col sticky top-0 z-40 bg-[#EEF3F7] backdrop-blur pt-6 px-6 py-2 gap-4">
                    <h1 className="text-2xl font-bold text-gray-900">Material Consumption vs Task Plan</h1>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Project Selector */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setIsProjectOpen(!isProjectOpen);
                                    setIsStageOpen(false);
                                    setIsBlockOpen(false);
                                }}
                                className="flex items-center justify-between gap-8 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors min-w-[240px]"
                            >
                                <span className="font-semibold text-gray-800">{selectedProject}</span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isProjectOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isProjectOpen && (
                                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-100">
                                    {projects.map((option) => (
                                        <button
                                            key={option}
                                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                                            onClick={() => {
                                                setSelectedProject(option);
                                                setIsProjectOpen(false);
                                            }}
                                        >
                                            {option}
                                            {selectedProject === option && <Check size={16} className="text-green-600" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Date Selector */}
                        <div className="relative z-40">
                            <DatePicker
                                selected={selectedDate}
                                onChange={(date) => setSelectedDate(date)}
                                customInput={<CustomDateInput />}
                                dateFormat="dd MMM yyyy"
                                maxDate={new Date()}
                                popperPlacement="bottom-start"
                            />
                        </div>

                        {/* Stage Selector */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setIsStageOpen(!isStageOpen);
                                    setIsProjectOpen(false);
                                    setIsBlockOpen(false);
                                }}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors text-gray-700 min-w-[140px] justify-between"
                            >
                                <span className="font-medium text-sm">{selectedStage}</span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isStageOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isStageOpen && (
                                <div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-100">
                                    {stages.map((option) => (
                                        <button
                                            key={option}
                                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                                            onClick={() => {
                                                setSelectedStage(option);
                                                setIsStageOpen(false);
                                            }}
                                        >
                                            {option}
                                            {selectedStage === option && <Check size={16} className="text-green-600" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Block Selector */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setIsBlockOpen(!isBlockOpen);
                                    setIsProjectOpen(false);
                                    setIsStageOpen(false);
                                }}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors text-gray-700 min-w-[140px] justify-between"
                            >
                                <span className="font-medium text-sm">{selectedBlock}</span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isBlockOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isBlockOpen && (
                                <div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-100 rounded-lg shadow-xl py-1 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-100">
                                    {blocks.map((option) => (
                                        <button
                                            key={option}
                                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700 flex justify-between items-center"
                                            onClick={() => {
                                                setSelectedBlock(option);
                                                setIsBlockOpen(false);
                                            }}
                                        >
                                            {option}
                                            {selectedBlock === option && <Check size={16} className="text-green-600" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex-1 text-right">
                            <span className="px-3 py-1 bg-green-50 text-green-600 text-xs font-medium rounded-full border border-green-200">
                                3 Items need attention
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="flex flex-col gap-6 pt-6 px-6 w-full">
                    <ConsumptionList tasks={inventoryReportData.tasks} />

                    <ConsumptionInsight
                        insights={inventoryReportData.insights}
                        recommendation={inventoryReportData.recommendation}
                    />
                </div>
            </div >
        </Layout >
    );
};

export default InventoryReport;
